import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import useApi from '../hooks/useApi';
import useAuth from '../hooks/useAuth';
import AuthService from '../services/auth.service';
import Autocomplete from 'react-google-autocomplete';
import { useLoadScript } from '@react-google-maps/api';
import ZoneService from '../services/zone.service';
import { useNotifications } from '../context/NotificationProvider';
import { useTranslation } from 'react-i18next';
import localizedTitle from '../utils/localizedTitle';
import infoIcon from "../assets/images/icon-info.svg";
import Modal from "react-modal";
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import { buildEcommerceObject, buildUserDataObject } from "../utils/gtmEcommerce";
import { buildGtmEventFields } from "../utils/gtmEventFields";
import { getGiftConfigurationPricing } from '../utils/giftConfigurationPricing';



const customStyles = {
    content: {
        width: "33%",
        top: "50%",
        padding: "40px",
        left: "50%",
        right: "auto",
        bottom: "auto",
        borderRadius: "10px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        maxWidth: "450px",
        margin: "auto",
        textAlign: "center",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
    },
};

const libraries = ['places'];

const Voucher = () => {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });
    const navigate = useNavigate();
    const { auth } = useAuth();
    const [trackingUser, setTrackingUser] = useState(auth?.user || null);
    const location = useLocation();
    const selectedGift = location.state?.selectedGift;
    const { t, i18n } = useTranslation('voucher');
    const [address, setAddress] = useState({
        street: '',
        city: '',
        zip_code: '',
        street_number: '',
        state: '',
        state_code: '',
        country: 'Switzerland',
        country_code: 'CH',
        lat: null,
        lng: null,
    });
    const api = useApi();
    const notify = useNotifications();
    const [showAddressField, setShowAddressField] = useState(false);
    const [voucherDetails, setVoucherDetails] = useState({
        client_id: auth?.user?.id,
        from_name: '',
        recipient_name: '',
        recipient_email: '',
        message: '',
        configuration: '',
    });
    const [modalIsOpen, setIsOpen] = useState(false);
    const [isConfigurationMenuOpen, setIsConfigurationMenuOpen] = useState(false);
    const configurationMenuRef = useRef(null);
    const closeModal = () => setIsOpen(false);
    const openModal = () => {
        setIsOpen(true)
    };

    useEffect(() => {
        setTrackingUser(auth?.user || null);
    }, [auth?.user]);

    useEffect(() => {
        if (!isConfigurationMenuOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!configurationMenuRef.current?.contains(event.target)) {
                setIsConfigurationMenuOpen(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsConfigurationMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isConfigurationMenuOpen]);

    const hasPrimaryAddress = (user) =>
        Boolean(
            user?.primary_address?.id
            || user?.primary_address?.street
            || user?.primary_address?.zip_code
            || user?.primaryAddress?.id
            || user?.primaryAddress?.street
            || user?.primaryAddress?.zip_code
        );

    const resolveTrackingUser = async () => {
        if (!auth?.user?.id) {
            return trackingUser || auth?.user || null;
        }

        const currentUser = trackingUser || auth?.user;
        if (hasPrimaryAddress(currentUser)) {
            return currentUser;
        }

        try {
            const response = await AuthService.me(
                { id: auth.user.id, with: "primary_address" },
                api
            );
            const nextUser = response?.user || currentUser;
            setTrackingUser(nextUser || null);
            return nextUser || currentUser || null;
        } catch (_) {
            return currentUser || null;
        }
    };
    // console.log('selectedGift', selectedGift)
    const getLocalized = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
            const lang = i18n?.language || 'en';
            return val[lang] || val.fr || val.en || '';
        }
        return String(val);
    };

    const handleToggle = () => {
        setShowAddressField(!showAddressField);
        if (!showAddressField) openModal();
        setAddress({
            street: '',
            city: '',
            zip_code: '',
            street_number: '',
            state: '',
            state_code: '',
            country: 'Switzerland',
            country_code: 'CH',
            lat: null,
            lng: null,
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "configuration") {
            const idx = parseInt(value, 10);
            const selectedConfig = !isNaN(idx) ? selectedGift.configuration[idx] : null;

            if (selectedConfig) {
                setVoucherDetails((prevDetails) => ({
                    ...prevDetails,
                    configuration: selectedConfig,
                }));
            }
        } else {
            setVoucherDetails((prevDetails) => ({
                ...prevDetails,
                [name]: value,
            }));
        }
    };

    const formatChf = (value) => {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
            return value;
        }

        return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
    };

    const getConfigurationLabel = (config) => {
        const pricing = getGiftConfigurationPricing(config);
        const title = localizedTitle(config, i18n);
        const valueLabel = pricing.hasDiscountedPurchase
            ? ` - ${t('voucher:redeem_value')} ${formatChf(pricing.redemptionValue)} CHF`
            : '';

        return `${formatChf(pricing.purchasePrice)} CHF${valueLabel} - ${title}`;
    };

    const handleConfigurationSelect = (index) => {
        const selectedConfig = selectedGift?.configuration?.[index];

        if (!selectedConfig) {
            return;
        }

        setVoucherDetails((prevDetails) => ({
            ...prevDetails,
            configuration: selectedConfig,
        }));
        setIsConfigurationMenuOpen(false);
    };

    const checkZipCodeInRange = async (zip_code) => {
        try {
            const zoneResponse = await ZoneService.index(null, api, auth?.accessToken ? false : true);

            if (zoneResponse) {
                const zipNumeric = parseInt(zip_code, 10);
                let isInRange = false;

                for (const zone of zoneResponse) {

                    if (isZipCodeInRange(zipNumeric, zone.zip_code)) {
                        isInRange = true;
                        break;
                    }
                }
                return isInRange;
            } else {
                console.error("Erreur lors de la récupération des zones.");
            }
        } catch (error) {
            console.error("Erreur lors de l'appel de ZoneService.index :", error);
        }
    };

    function isZipCodeInRange(zipCode, range) {
        const ranges = range.split(',');
        for (let element of ranges) {
            const [start, end] = element.split('-').map(Number);

            const zip = parseInt(zipCode, 10);

            return start === zip || (zip > start && zip <= end);
        }
    }

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!voucherDetails.configuration) {
            notify.warn(t('voucher:select_gift_offer'));
            return;
        }

        if (address.zip_code) {
            // console.log('add zip', address.zip_code)
            const isGiftZone = await checkZipCodeInRange(address.zip_code);
            if (!isGiftZone) {
                notify.warn(t('make_sure_recipient_in_serviced_zones'));
                // return false;
            }
            if (!address.zip_code) {
                notify.warn(t('please_enter_postal_code'));
            }
        }

        try {
            const resolvedTrackingUser = await resolveTrackingUser();
            const selectedConfig = voucherDetails?.configuration;
            const selectedPricing = getGiftConfigurationPricing(selectedConfig);
            const basePrice = selectedPricing.purchasePrice;
            const itemId = String(
                selectedConfig?.id ??
                selectedConfig?.product_id ??
                selectedGift?.id ??
                selectedConfig?.type ??
                "gift_offer"
            );
            const giftName = getLocalized(selectedGift?.name);
            const configTitle = localizedTitle(selectedConfig, i18n);
            const locationId = address?.place_id || address?.zip_code || undefined;
            const itemCategory = /hotel|h[oô]tel/i.test(giftName || "")
                ? "hotel_partenaire"
                : "a_domicile";
            const trackingUserData = buildUserDataObject({
                user: resolvedTrackingUser,
                address,
                emailAddress: resolvedTrackingUser?.email || auth?.user?.email,
                phoneNumber: resolvedTrackingUser?.phone || auth?.user?.phone,
                isConnectedUser: Boolean(auth?.user?.id),
            });

            pushDataLayerEvent(
                "add_to_cart",
                {
                    user_data: trackingUserData,
                    ...buildGtmEventFields({
                        language: i18n.language,
                        user: resolvedTrackingUser || auth?.user,
                    }),
                    ecommerce: buildEcommerceObject({
                        currency: "CHF",
                        value: basePrice,
                        items: [
                            {
                                item_id: itemId,
                                item_name: configTitle || giftName || "gift_offer",
                                price: basePrice,
                                quantity: 1,
                                discount: 0,
                                item_brand: "Flyspa",
                                item_category: itemCategory,
                                item_category2: configTitle || selectedConfig?.type || undefined,
                                item_list_id: "massage_services",
                                item_list_name: giftName || "Bons Cadeaux & Packages",
                                location_id: locationId,
                                index: 0,
                            },
                        ],
                    }),
                },
                {
                    dedupeKey: createEventDedupeKey(
                        "add_to_cart",
                        "gift_flow",
                        selectedGift?.id,
                        itemId,
                        basePrice,
                        locationId
                    ),
                    dedupeTtlMs: 30000,
                }
            );
        } catch (trackingError) {
            console.error("Failed to push gift add_to_cart event:", trackingError);
        }

        // console.log('state gift submit', { state: { selectedGift, address: address, giftDetails: voucherDetails } })
        navigate('/paymentgift', { state: { selectedGift, address: address, giftDetails: voucherDetails } });

    }

    if (loadError) return <div>{t('voucher:error_loading_maps')}</div>;
    if (!isLoaded) return <div>{t('voucher:loading_maps')}</div>;

    const selectedConfigurationIndex = selectedGift?.configuration?.findIndex(
        (config) => config === voucherDetails.configuration
    ) ?? -1;
    const selectedConfigurationLabel = selectedConfigurationIndex >= 0
        ? getConfigurationLabel(selectedGift.configuration[selectedConfigurationIndex])
        : t('voucher:select_gift_offer');
    const giftValidityDisplay = i18n.language?.startsWith('en') ? '2 years' : '2 ans';

    const handlePlaceSelect = (place) => {
        const addressComponents = place.address_components;
        const newAddress = {
            street: '',
            city: '',
            zip_code: '',
            street_number: '',
            state: '',
            state_code: '',
            country: 'Switzerland',
            country_code: 'CH',
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
        };
        // console.log("places", addressComponents)
        for (const component of addressComponents) {
            const componentType = component.types[0];
            switch (componentType) {
                case 'street_number':
                    newAddress.street = `${component.long_name} ${newAddress.street}`;
                    break;
                case 'route':
                    newAddress.street = `${newAddress.street} ${component.long_name}`;
                    break;
                case 'locality':
                    newAddress.city = component.long_name;
                    break;
                case 'postal_code':
                    newAddress.zip_code = component.long_name;
                    break;
                case 'administrative_area_level_1':
                    newAddress.state = component.long_name;
                    newAddress.state_code = component.short_name;
                    break;
                case 'country':
                    newAddress.country = component.long_name;
                    newAddress.country_code = component.short_name;
                    break;
                /*  case 'street_number':
                   newAddress.street_number = component.long_name;
                   break; */
                default:
                    break;
            }
        }

        setAddress(newAddress);
    };

    return (
        <>
            <Header />

            <div className="container buy-container gift-page">
                <h2>{t('giftselected_title')}</h2>
                {/* <h2>{selectedGift.name}</h2>
                <h3>{selectedGift.description}</h3> */}
                <h3>{t('giftselected_description')}</h3>
                <form onSubmit={handleFormSubmit}>
                    <div className="form-group">
                        <label htmlFor="configuration">{t('voucher:gift_offers')}</label>
                        <div
                            ref={configurationMenuRef}
                            className={`voucher-select${isConfigurationMenuOpen ? ' is-open' : ''}`}
                        >
                            <button
                                id="configuration"
                                type="button"
                                className="form-control buy-select-control voucher-select-control"
                                aria-haspopup="listbox"
                                aria-expanded={isConfigurationMenuOpen}
                                onClick={() => setIsConfigurationMenuOpen((open) => !open)}
                            >
                                <span className={`voucher-select-control__label${selectedConfigurationIndex < 0 ? ' is-placeholder' : ''}`}>
                                    {selectedConfigurationLabel}
                                </span>
                            </button>

                            {isConfigurationMenuOpen && (
                                <ul className="voucher-select-menu" role="listbox" aria-labelledby="configuration">
                                    {selectedGift.configuration.map((config, index) => (
                                        <li key={index} role="presentation">
                                            <button
                                                type="button"
                                                className={`voucher-select-option${selectedConfigurationIndex === index ? ' is-selected' : ''}`}
                                                role="option"
                                                aria-selected={selectedConfigurationIndex === index}
                                                onClick={() => handleConfigurationSelect(index)}
                                            >
                                                {getConfigurationLabel(config)}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="validity">{t('voucher:validity')}</label>
                        <div
                            id="validity"
                            className="form-control gift-validity-display"
                            aria-readonly="true"
                        >
                            <span className="gift-validity-display__period">{giftValidityDisplay}</span>
                        </div>
                        <p className='gift-cond'>{t('voucher:condition')}</p>
                    </div>
                    <div className="form-group">
                        <label htmlFor="from_name">{t('voucher:gift_from')}</label>
                        <input
                            type="text"
                            id="from_name"
                            name="from_name"
                            className="form-control"
                            placeholder={t('voucher:add_name')}
                            value={voucherDetails.from_name}
                            required
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="recipient_name">{t('voucher:recipient_details')}</label>
                        <input
                            type="text"
                            id="recipient_name"
                            name="recipient_name"
                            className="form-control"
                            placeholder={t('voucher:add_recipient_name')}
                            value={voucherDetails.recipient_name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <br />
                        <input
                            type="email"
                            id="recipient_email"
                            name="recipient_email"
                            className="form-control"
                            placeholder={t('voucher:add_recipient_email')}
                            value={voucherDetails.recipient_email}
                            required
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea
                            type="text"
                            id="message"
                            name="message"
                            className="form-control"
                            placeholder={t('voucher:add_message')}
                            value={voucherDetails.message}
                            onChange={handleInputChange}
                            style={{ height: '100px' }}
                        />
                    </div>
                    <div
                        className="giftCard"
                        style={{ borderColor: showAddressField ? '#393D44' : '' }}
                    >
                        <div
                            className="text-wrapper"
                            style={{ color: showAddressField ? '#393D44' : '' }}
                        >
                            {t('voucher:physical_gift_card')}
                        </div>
                        <label className="switch">
                            <input
                                type="checkbox"
                                id="toggle"
                                onChange={handleToggle}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                    {showAddressField && (
                        <div className="form-group">
                            <label htmlFor="street">{t('voucher:enter_address')}</label>
                            <Autocomplete
                                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                onPlaceSelected={handlePlaceSelect}
                                options={{
                                    types: ['address'],
                                    componentRestrictions: { country: 'CH' },
                                }}
                                placeholder={t('voucher:enter_address_placehold')}
                                style={{
                                    width: '100%',
                                    padding: '0 15px',
                                    height: '60px',
                                    border: '0',
                                    borderRadius: '20px',
                                    boxShadow: '0px 8px 50px 0px rgba(0, 0, 0, 0.06)',
                                }}
                            />
                        </div>
                    )}
                    {address.lat && address.lng && (
                        <>
                            <div className="form-group">
                                <input
                                    type="hidden"
                                    id="street"
                                    className="form-control"
                                    value={address.street}
                                    onChange={(e) => setAddress({ ...address, street: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <input
                                    type="hidden"
                                    id="state"
                                    className="form-control"
                                    value={address.state}
                                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                {(!address.zip_code || address.zip_code.length < 8) && (
                                    <>
                                        <label htmlFor="postalCode">{t('voucher:postal_code')}</label>
                                        <input
                                            type="text"
                                            id="postalCode"
                                            className="form-control"
                                            value={address.zip_code}
                                            onChange={(e) => setAddress({ ...address, zip_code: e.target.value })}
                                            required
                                        />
                                    </>
                                )}
                            </div>
                            <div className="form-group">
                                <input
                                    type="hidden"
                                    id="city"
                                    className="form-control"
                                    value={address.city}
                                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    <button type="submit" className="btn btn-primary gift-btn">
                        {t('voucher:continue_to_payment')}
                    </button>
                </form>
            </div>
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                style={customStyles}
                contentLabel="Example Modal"
            >
                <div className="zone-not-served-container">
                    <div className="popup-box">
                        <button onClick={closeModal} className="close-btn"><img src={infoIcon} alt="Info" /></button>
                        <p>
                            {t('card_info')}
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default Voucher;
